import { Drawer, Pagination, TableRow } from '@mui/material';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { Outlet, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useGetTransactionsQuery } from '@/api/v1';
import { Clickable } from '@/components/atoms/Clickable';
import { NoResultsView } from '@/components/atoms/NoResultsView';
import { TableCellErrorBoundary } from '@/components/atoms/TableCellErrorBoundary';
import { TableContentSelector } from '@/components/atoms/TableContentSelector';
import { TableRowErrorBoundary } from '@/components/atoms/TableRowErrorBoundary';
import { HeaderCell } from '@/components/molecules/HeaderCell';
import { TransactionTableCell } from '@/components/molecules/TransactionTableCell';
import { ActivitySearchBar } from '@/components/organisms/ActivitySearchBar';
import { MainScreen } from '@/components/organisms/MainScreen';
import { useCustomer } from '@/hooks/useCustomer';
import { useTranslator } from '@/hooks/useTranslator';
import { useTypedSearchParams } from '@/hooks/useTypedSearchParams';
import { createHeaders } from '@/Screens/Dashboard/Activity/headers';
import { NoActivityView } from '@/Screens/Dashboard/Activity/NoActivityView';
import { GetTransactionsFilters } from '@/types/getTransactionsFilters';
import { HeaderItem } from '@/types/headerItem';
import { Page } from '@/types/page';
import { Transaction } from '@/types/transaction';
import { computeSortingValue, parseSort } from '@/utility/sorting';

type TransactionRow = Transaction;

export const Activity: React.FC = (): React.ReactElement => {
  const [search] = useSearchParams();
  const [filters, setFilters] = useTypedSearchParams<GetTransactionsFilters>(
    GetTransactionsFilters.initial(),
  );

  const tr = useTranslator();
  const customer = useCustomer();
  const navigate = useNavigate();

  const { transactionId = '' } = useParams<{ readonly transactionId: string | undefined }>();
  const { data: transactionsPage = Page.empty(), status: fetchingStatus } = useGetTransactionsQuery(
    filters,
    {
      skip: !customer.beneficiaryId,
    },
  );

  const sorting = React.useMemo((): Record<string, 'asc' | 'desc'> => {
    const sort = filters.sort;
    if (sort.length > 0) {
      if (typeof sort === 'string') {
        return parseSort([sort]);
      } else {
        return parseSort(sort);
      }
    }

    return {
      createdDate: 'desc',
      type: 'desc',
      amount: 'desc',
    };
  }, [filters.sort]);

  const {
    content: transactions,
    totalPages,
    totalElements,
    pageable: { pageNumber },
  } = transactionsPage;

  const handleSort = React.useCallback(
    (column: string): void => {
      setFilters((filters: GetTransactionsFilters): GetTransactionsFilters => {
        const sort = typeof filters.sort === 'string' ? [filters.sort] : filters.sort;

        const current = sort
          .map((item: string): [string, string] => {
            const [targetColumn, direction] = item.split(',');
            return [targetColumn, direction];
          })
          .filter(
            (item: [string, string]): item is [string, 'asc' | 'desc'] =>
              item[1] === 'asc' || item[1] === 'desc',
          )
          .find(([targetColumn]: [string, 'asc' | 'desc']): boolean => {
            return targetColumn === column;
          }, []);

        if (!current) {
          return {
            ...filters,
            sort: [`${column},asc`, ...sort],
            page: 0,
          };
        }
        const [, direction] = current;
        const computedDirection = computeSortingValue(direction);

        return {
          ...filters,
          sort: [
            `${column},${computedDirection}`,
            ...sort.filter((item: string): boolean => item.split(',')?.[0] !== column),
          ],
          page: 0,
        };
      });
    },
    [setFilters],
  );

  const headers = React.useMemo(
    (): readonly HeaderItem<TransactionRow>[] => createHeaders(tr),
    [tr],
  );
  const rows = React.useMemo((): readonly TransactionRow[] => transactions, [transactions]);

  const onRowClick = React.useCallback(
    (transactionId: string): void => {
      // Might not be the best solution, but it does work -\_o_/-
      navigate({
        pathname: transactionId,
        search: search.toString(),
      });
    },
    [navigate, search],
  );

  const closeTransactionDetails = React.useCallback((): void => {
    navigate({
      pathname: '.',
      search: search.toString(),
    });
  }, [navigate, search]);

  const onPageChange = React.useCallback(
    (_: React.ChangeEvent<unknown>, page: number): void => {
      setFilters(
        (filters: GetTransactionsFilters): GetTransactionsFilters => ({
          ...filters,
          page: page,
        }),
      );
    },
    [setFilters],
  );

  const filtersCount = React.useMemo((): number => countFilters(filters), [filters]);

  return (
    <MainScreen title={tr('ACTIVITY')}>
      <Box display="flex" flexDirection="column" gap={4} height="100%">
        <ActivitySearchBar filtersCount={filtersCount} />
        <Paper elevation={3} sx={styles.paper}>
          <TableContentSelector
            status={fetchingStatus}
            noContentView={<NoActivityView />}
            noResultsView={
              <NoResultsView
                title={tr('ZERO_RESULTS_FOUND')}
                message={tr(
                  'TRY_CHANGING_YOUR_APPLIED_FILTERS',
                  tr('ACTIVITY').toLocaleLowerCase(),
                )}
              />
            }
            filtersCount={filtersCount}
            data={rows}>
            <Table sx={styles.table}>
              <colgroup>
                {widths.map(
                  (width: number, index: number): React.ReactElement => (
                    <col key={index} width={width / totalWidth} />
                  ),
                )}
              </colgroup>
              <TableHead>
                <TableRow>
                  {headers.map(
                    (item: HeaderItem<TransactionRow>): React.ReactElement => (
                      <HeaderCell
                        key={item.key}
                        item={item}
                        sorting={sorting}
                        onClick={handleSort}
                      />
                    ),
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row: TransactionRow): React.ReactElement => {
                  return (
                    <TableRowErrorBoundary
                      key={row.id}
                      columnCount={headers.length}
                      fallbackMessage="Unable to display transaction data">
                      <Clickable data={row.id} onClick={onRowClick}>
                        <TableRow sx={styles.row} hover={true}>
                          {headers.map(
                            (header: HeaderItem<TransactionRow>): React.ReactElement => (
                              <TableCellErrorBoundary
                                key={header.key}
                                sx={styles.cell}
                                fallbackMessage="N/A">
                                <TransactionTableCell
                                  transaction={row}
                                  field={header.key}
                                  sx={styles.cell}
                                />
                              </TableCellErrorBoundary>
                            ),
                          )}
                        </TableRow>
                      </Clickable>
                    </TableRowErrorBoundary>
                  );
                })}
              </TableBody>
            </Table>
          </TableContentSelector>
        </Paper>
        {totalElements > 0 && (
          <Box display="flex" alignItems="center" justifyContent="center">
            <Pagination count={totalPages} page={pageNumber + 1} onChange={onPageChange} />
          </Box>
        )}
      </Box>

      <Drawer open={!!transactionId} anchor="right" onClose={closeTransactionDetails}>
        <Outlet />
      </Drawer>
    </MainScreen>
  );
};

const styles: Record<string, SystemStyleObject> = {
  paper: {
    flex: 1,
  },
  loadingContainer: {
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  table: {
    width: '100%',
    tableLayout: 'fixed',
  },
  cell: {
    minWidth: 0,
    '&:first-of-type': {
      pl: 5,
    },
    '&:last-of-type': {
      pr: 5,
    },
  },
  cellContent: {
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  row: {
    cursor: 'pointer',
  },
};

const widths = [136, 99, 90, 200, 116, 116, 115];
const totalWidth = widths.reduce((total: number, width: number): number => total + width, 0);

const countFilters = (filters: GetTransactionsFilters): number => {
  const initial = GetTransactionsFilters.initial();

  return Object.entries(filters).reduce(
    (totalCount: number, [key, value]: [string, string]): number => {
      switch (key) {
        case 'id':
        case 'type':
        case 'status':
        case 'fromDate':
        case 'toDate':
        case 'amount':
        case 'currency':
        case 'recipientId':
          return totalCount + (value === initial[key] ? 0 : 1);
        default:
          return totalCount;
      }
    },
    0,
  );
};
